import React, { useState, useEffect, useRef } from "react";
import { getClassesAPI } from "../services/portal/admin/classService";
import Loader from "../components/Loader";
import { formatDate } from "../utility/Common";

const Home1 = () => {
  const [loading, setLoading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const [isThirdSectionVisible, setIsThirdSectionVisible] = useState(false);
  const secondSectionRef = useRef(null);
  const thirdSectionRef = useRef(null);

  useEffect(() => {
    loadClasses();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    const currentRef = secondSectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsThirdSectionVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    const currentRef = thirdSectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const loadClasses = async () => {
    setLoading(true);
    try {
      const res = await getClassesAPI();
      if (res && res.IsSuccessful && res.Data) {
        setClasses(res.Data);
      }
    } catch (error) {
      console.error("Error loading classes:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* First Section - Logo Full Height */}
      <section className="h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <img
            src="images/logo.png"
            alt="Royal Econ Logo"
            className="w-64 md:w-80 lg:w-96 mx-auto animate-pulse"
          />
        </div>
      </section>

      {/* Second Section - Classes and Logo with Animations */}
      <section
        ref={secondSectionRef}
        className="min-h-screen bg-black py-20 px-4 sm:px-6 lg:px-8"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left Side - Class Cards */}
            <div>
              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <Loader />
                </div>
              ) : classes.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-400 text-lg">No classes available</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-6">
                  {classes.map((classItem, index) => (
                    <div
                      key={index}
                      className={`bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition duration-300 border-l-4 border-primary-600 ${
                        isVisible
                          ? "animate-slide-in-right opacity-100"
                          : "opacity-0 translate-x-full"
                      }`}
                      style={{
                        animationDelay: `${index * 0.1}s`,
                        animationFillMode: "both",
                      }}
                    >
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-semibold text-primary-600 bg-primary-50 px-3 py-1 rounded-full">
                        {classItem.ClassType?.Name || "Class"}
                      </span>
                      <span
                        className={`text-sm font-semibold px-2 py-1 rounded-full ${
                          classItem.Status === 1
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {classItem.Status === 1 ? "Active" : "Inactive"}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {classItem.Name}
                    </h3>
                    <div className="space-y-2 text-gray-600">
                      <div className="flex items-center">
                        <svg
                          className="w-5 h-5 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        <span className="font-semibold text-sm">
                          Start: {formatDate(classItem.StartDate)}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <svg
                          className="w-5 h-5 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        <span className="font-semibold text-sm">
                          End: {formatDate(classItem.EndDate)}
                        </span>
                      </div>
                    </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right Side - Logo */}
            <div className="flex items-center justify-center lg:sticky lg:top-20">
              <div
                className={`${
                  isVisible
                    ? "animate-slide-in-down opacity-100"
                    : "opacity-0 -translate-y-full"
                }`}
                style={{
                  animationDelay: "0.3s",
                  animationFillMode: "both",
                }}
              >
                <img
                  src="images/logo.png"
                  alt="Royal Econ Logo"
                  className="w-full max-w-md mx-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Third Section - Harsha Image and Logo */}
      <section
        ref={thirdSectionRef}
        className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black py-20 px-4 sm:px-6 lg:px-8"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Harsha Image */}
            <div className="flex items-center justify-center">
              <div
                className={`${
                  isThirdSectionVisible
                    ? "animate-slide-in-right opacity-100"
                    : "opacity-0 translate-x-full"
                }`}
                style={{
                  animationDelay: "0.2s",
                  animationFillMode: "both",
                }}
              >
                <img
                  src="images/harsha.png"
                  alt="Harsha"
                  className="w-full max-w-lg mx-auto rounded-lg shadow-2xl"
                />
              </div>
            </div>

            {/* Right Side - Logo */}
            <div className="flex items-center justify-center">
              <div
                className={`${
                  isThirdSectionVisible
                    ? "animate-slide-in-down opacity-100"
                    : "opacity-0 -translate-y-full"
                }`}
                style={{
                  animationDelay: "0.4s",
                  animationFillMode: "both",
                }}
              >
                <img
                  src="images/logo.png"
                  alt="Royal Econ Logo"
                  className="w-full max-w-md mx-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home1;
